import { useState, useEffect, useCallback, useRef } from 'react'
import { db } from '../../../firebase'
import { doc, onSnapshot, setDoc, serverTimestamp } from 'firebase/firestore'

const DEFAULT_CONFIG = {
  fields: {
    barcode: true,
    barcodeNumber: true,
    title: true,
    price: true,
    weight: true,
    sku: false,
    karat: false,
    productType: false
  },
  fontSize: {
    price: 9,
    title: 6,
    sku: 5.5,
    weight: 6,
    karat: 5,
    productType: 5
  },
  alignment: {
    left: 'left',
    right: 'right'
  },
  barcode: {
    show: true,
    format: 'EAN8',
    width: 0.7,
    height: 22,
    displayValue: false
  },
  label: {
    width: '2.25in',
    height: '0.5in'
  }
}

export function useTagConfig() {
  const [tagConfig, setTagConfig] = useState(DEFAULT_CONFIG)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const debounceRef = useRef(null)
  const configRef = useRef(DEFAULT_CONFIG)

  useEffect(() => {
    const docRef = doc(db, 'app_config', 'tag_settings')
    const unsubscribe = onSnapshot(
      docRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data()
          const next = {
            fields: { ...DEFAULT_CONFIG.fields, ...data.fields },
            fontSize: { ...DEFAULT_CONFIG.fontSize, ...data.fontSize },
            alignment: { ...DEFAULT_CONFIG.alignment, ...data.alignment },
            barcode: { ...DEFAULT_CONFIG.barcode, ...data.barcode },
            label: { ...DEFAULT_CONFIG.label, ...data.label }
          }
          configRef.current = next
          setTagConfig(next)
        }
        setLoading(false)
      },
      (err) => {
        console.warn('Failed to load tag config:', err)
        setLoading(false)
      }
    )

    return () => unsubscribe()
  }, [])

  useEffect(() => {
    configRef.current = tagConfig
  }, [tagConfig])

  const updateTagConfig = useCallback((updates) => {
    setTagConfig(prev => {
      const next = { ...prev }
      for (const key of Object.keys(updates)) {
        if (typeof updates[key] === 'object' && !Array.isArray(updates[key])) {
          next[key] = { ...prev[key], ...updates[key] }
        } else {
          next[key] = updates[key]
        }
      }
      configRef.current = next

      if (debounceRef.current) clearTimeout(debounceRef.current)
      debounceRef.current = setTimeout(() => {
        const docRef = doc(db, 'app_config', 'tag_settings')
        setDoc(docRef, { ...next, updatedAt: serverTimestamp() }, { merge: true })
          .catch(err => console.error('Failed to save tag config:', err))
      }, 500)

      return next
    })
  }, [])

  const saveTagConfig = useCallback(async () => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
      debounceRef.current = null
    }
    setSaving(true)
    try {
      const docRef = doc(db, 'app_config', 'tag_settings')
      await setDoc(docRef, { ...configRef.current, updatedAt: serverTimestamp() }, { merge: true })
    } finally {
      setSaving(false)
    }
  }, [])

  return { tagConfig, loading: loading, saving, updateTagConfig, saveTagConfig, DEFAULT_CONFIG }
}

export default useTagConfig
